import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, MapPin } from "lucide-react";
import Loading from "@/components/Loading";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { OrgJobPostDetailDialog } from "./org-job-post-detail-dialog";

export const InterestedJobPostsDialog = ({
  userId,
  organizationBusinessUnitId,
  providerName,
  open,
  onOpenChange,
  onRestoreFocus,
}: {
  userId: string;
  organizationBusinessUnitId: string;
  providerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestoreFocus?: () => void;
}) => {
  const trpc = useTRPC();
  const { data, isLoading, error } = useQuery(
    trpc.jobPost.getInterestedJobPostsForUser.queryOptions(
      {
        userId,
        organizationBusinessUnitId,
      },
      { enabled: open },
    ),
  );
  const [viewingJobPostId, setViewingJobPostId] = useState<string | null>(null);

  return (
    <>
      <Dialog open={open && !viewingJobPostId} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-lg max-h-[80vh] flex flex-col gap-0 p-0"
          onCloseAutoFocus={(event) => {
            if (!onRestoreFocus || viewingJobPostId) {
              return;
            }
            event.preventDefault();
            onRestoreFocus();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
            <DialogTitle>Job Interests — {providerName}</DialogTitle>
            <DialogDescription>
              {data && data.length > 0 ? (
                <>
                  {data.length} job {data.length === 1 ? "post" : "posts"}
                </>
              ) : (
                "Jobs this provider is interested in within your organization."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loading className="h-12 w-12" />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-sm text-gray-500">
                Failed to load job posts.
              </div>
            ) : !data || data.length === 0 ? (
              <div className="text-center p-8 text-sm text-gray-500">
                No interested job posts in this organization.
              </div>
            ) : (
              <div className="space-y-2">
                {data.map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors group"
                    onClick={() => setViewingJobPostId(post.id)}
                  >
                    <Briefcase className="h-4 w-4 mt-0.5 shrink-0 text-slate-400 group-hover:text-blue-500" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-slate-900 truncate block">
                        {post.title || "Untitled"}
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        {(post.city || post.stateCode) && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin className="h-3 w-3" />
                            {[post.city, post.stateCode]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        )}
                        {post.open !== null && (
                          <Badge
                            variant={post.open ? "default" : "secondary"}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {post.open ? "Open" : "Closed"}
                          </Badge>
                        )}
                      </div>
                      {post.decisionCreatedAt && (
                        <p className="text-[11px] text-slate-400 mt-1">
                          Interested{" "}
                          {format(
                            new Date(post.decisionCreatedAt),
                            "MMM d, yyyy",
                          )}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {viewingJobPostId && (
        <OrgJobPostDetailDialog
          jobId={viewingJobPostId}
          organizationBusinessUnitId={organizationBusinessUnitId}
          open={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setViewingJobPostId(null);
            }
          }}
        />
      )}
    </>
  );
};
